import React, { useEffect, useState, useRef } from 'react';
import { IoIosArrowForward, IoMdClose } from 'react-icons/io';
import { RxCross2 } from 'react-icons/rx';
import {
  FaFileUpload,
  FaSpinner,
  FaCheckCircle,
  FaTimesCircle,
} from 'react-icons/fa';
import { FaFile } from 'react-icons/fa6';
import toast from 'react-hot-toast';
import { FiDownload } from 'react-icons/fi';

import useDrivePicker from '../hooks/useGoogleDrivePicker';
import { DataState } from '../context/DataProvider';
import {
  google_client_id,
  google_api_key,
  GOOGLE_DRIVE_PICKER_ENABLED,
} from '../utils/keys';
import {
  uploadFilesDataSource,
  deleteFileDataSource,
  uploadFilesFromDriveDataSource,
  downloadFileDataSource,
} from '../services/api';
import SelectDataSource from './SelectDataSource';

const DataSourceModal = ({ closeModal }) => {
  const {
    userGoogleId,
    fetchDataSourceFiles: fetchFiles,
    allUserDataSourceFiles: files,
  } = DataState();

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingFiles, setDeletingFiles] = useState({});
  const [error, setError] = useState(null);

  const modalRef = useRef(null);

  const handleFileChange = (event) => {
    setSelectedFiles(Array.from(event.target.files));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select files to upload');
      return;
    }

    try {
      setLoading(true);
      const response = await uploadFilesDataSource(userGoogleId, selectedFiles);
      if (response.error) {
        setError(response.error);
      } else {
        toast.success('Files uploaded successfully');
        setSelectedFiles([]);
        fetchFiles();
      }
    } catch (error) {
      setError('Error uploading files');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (fileId) => {
    setDeletingFiles((prev) => ({ ...prev, [fileId]: true }));
    try {
      const response = await deleteFileDataSource(fileId, userGoogleId);
      if (response.error) {
        setError(response.error);
      } else {
        toast.success('File deleted successfully');
        fetchFiles();
      }
    } catch (error) {
      setError('Error deleting file');
    } finally {
      setDeletingFiles((prev) => ({ ...prev, [fileId]: false }));
    }
  };

  const handleDownload = async (fileId) => {
    try {
      const response = await downloadFileDataSource(fileId, userGoogleId);
      if (!response.error) {
        window.open(response.url, '_blank');
      } else {
        setError(response.error);
      }
    } catch (error) {
      setError('Error downloading file');
    }
  };

  const [openPicker] = useDrivePicker();
  const handleOpenPicker = () => {
    openPicker({
      clientId: google_client_id,
      developerKey: google_api_key,
      viewId: 'DOCS',
      showUploadView: true,
      showUploadFolders: true,
      supportDrives: true,
      multiselect: true,
      callbackFunction: async (data) => {
        if (data.action === 'picked') {
          const fileIds = data.docs.map((doc) => doc.id);
          const response = await uploadFilesFromDriveDataSource(
            userGoogleId,
            fileIds,
          );
          if (!response.error) {
            toast.success('Files uploaded from Google Drive');
            fetchFiles();
          }
        }
      },
    });
  };

  useEffect(() => {
    fetchFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickOutside = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      closeModal();
    }
  };
  // Function to map status to color and icons
  const getFileStatusStyles = (status) => {
    if (status && status.startsWith('failed')) {
      if (status === 'failed') {
        // Complete failure
        return {
          color: 'text-red-500',
          icon: <FaTimesCircle />,
          text: 'Failed',
        };
      } else {
        // Partial failure (still retrying)
        return {
          color: 'text-orange-500',
          icon: <FaSpinner className="animate-spin" />,
          text: status,
        };
      }
    }

    switch (status) {
      case 'queued':
        return {
          color: 'text-[#5652CF]',
          icon: <FaSpinner className="animate-spin" />,
          text: 'Queued',
        };
      case 'processing':
        return {
          color: 'text-yellow-500',
          icon: <FaSpinner className="animate-spin" />,
          text: 'Processing',
        };
      case 'success':
        return {
          color: 'text-green-500',
          icon: <FaCheckCircle />,
          text: 'Success',
        };
      default:
        return { color: 'text-gray-500', icon: null, text: status };
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [selectedTab, setSelectedTab] = useState('select_file');
  const handleSelectTab = (tab) => {
    setSelectedTab(tab);
  };
  return (
    <>
      <button
        className="fixed inset-0 z-[110] block bg-black bg-opacity-50"
        onClick={closeModal}
      />
      <div className="bg-2 br-1 text-p-text fixed left-[50%] top-[50%] z-[120] flex max-h-[min(680px,calc(100vh-2rem))] min-h-96 w-5/6 translate-x-[-50%] translate-y-[-50%] flex-col rounded-lg border  p-8 py-6 shadow-2xl sm:max-w-[45rem]">
        {/* <div className="fixed top-0 left-0 z-[120] p-8 rounded-lg bg-2 border br-1 shadow-2xl flex flex-col w-screen h-screen py-6 text-p-text"> */}
        <div className="flex max-h-96 flex-1 flex-col gap-6">
          {/* <div className="flex flex-col gap-6 flex-1 max-w-4xl w-full mx-auto py-3"> */}
          <header className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <h2 className="p-text text-lg font-semibold">Data Source</h2>
            </div>
            <button
              onClick={closeModal}
              type="button"
              className="text-md rounded-[3px] text-xl font-semibold transition-colors duration-150 ease-in-out disabled:cursor-not-allowed"
            >
              <IoMdClose />
            </button>
          </header>

          <div className="hide-sidebar flex min-h-0 w-full flex-1 flex-col gap-4 overflow-y-auto">
            <div className="w-full">
              <div className="bg-3 br-1 mx-auto flex w-fit gap-1 rounded-full border p-1">
                <button
                  onClick={() => handleSelectTab('select_file')}
                  class={`flex min-h-7 items-center gap-1 rounded-full border-0 px-3.5 py-1 text-sm transition-all duration-200 disabled:cursor-not-allowed ${
                    selectedTab === 'select_file'
                      ? 'bg-1 hover:text-s-text text-p-text font-semibold'
                      : 'hover:text-s-text text-p-text bg-transparent font-normal'
                  }`}
                >
                  <span>Select File</span>
                </button>
                <button
                  onClick={() => handleSelectTab('upload_file')}
                  class={`flex min-h-7 items-center gap-1 rounded-full border-0 px-3.5 py-1 text-sm transition-all duration-200 disabled:cursor-not-allowed ${
                    selectedTab === 'upload_file'
                      ? 'bg-1 hover:text-s-text text-p-text font-semibold'
                      : 'hover:text-s-text text-p-text bg-transparent font-normal'
                  }`}
                >
                  <span>Upload File</span>
                </button>
              </div>
            </div>

            {selectedTab === 'upload_file' && (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  {GOOGLE_DRIVE_PICKER_ENABLED && (
                    <button
                      onClick={handleOpenPicker}
                      className="flex items-center justify-between gap-2 rounded-md bg-zinc-800 p-2 hover:bg-opacity-70 "
                    >
                      <div className="flex items-center gap-2">
                        <img
                          src="https://res.cloudinary.com/dsguoq6ad/image/upload/v1726229007/mentorEducationImages/lhupskidmwtibynwa0no.png"
                          className="h-5 w-5"
                          alt="Google Drive"
                        />
                        <span className="text-sm text-zinc-200">
                          Upload from Google Drive
                        </span>
                      </div>
                      <IoIosArrowForward />
                    </button>
                  )}
                  <div className="relative flex items-center justify-between gap-2 rounded-md bg-zinc-800 p-2 hover:bg-opacity-70">
                    <div className="flex items-center gap-2">
                      <FaFileUpload className="h-5 w-5 text-zinc-200" />
                      <span className="text-sm text-zinc-200">
                        Upload from Computer
                      </span>
                    </div>
                    <IoIosArrowForward />
                    <input
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <h3 className="mb-2 text-base font-semibold text-zinc-100">
                    Selected Files
                  </h3>
                  {selectedFiles.length > 0 ? (
                    <>
                      <ul className="space-y-2">
                        {selectedFiles.map((file, index) => (
                          <li
                            key={index}
                            className="flex items-center justify-between rounded-md bg-zinc-800 p-2 hover:bg-opacity-70"
                          >
                            <div className="flex max-w-[70%] items-center gap-2">
                              <FaFile className="text-zinc-200" />
                              <span
                                className="truncate text-sm text-zinc-200 transition-all hover:max-w-none hover:overflow-visible hover:whitespace-normal"
                                title={file.name}
                              >
                                {file.name}
                              </span>
                            </div>
                            <button
                              onClick={() =>
                                setSelectedFiles((prevFiles) =>
                                  prevFiles.filter((_, i) => i !== index),
                                )
                              }
                              className="text-red-400 hover:text-red-300"
                            >
                              <RxCross2 />
                            </button>
                          </li>
                        ))}
                      </ul>

                      <button
                        onClick={handleUpload}
                        className="ml-auto mt-4 flex items-center gap-1 rounded-lg bg-zinc-200 px-3 py-1 text-zinc-800"
                        // className={`mt-4 w-full py-2 rounded-md ${
                        //   loading
                        //     ? "bg-zinc-600 cursor-not-allowed"
                        //     : "bg-[#5566FF] hover:bg-[#5652CF]"
                        // }`}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <FaSpinner className="mr-2 mr-2 inline animate-spin" />{' '}
                            Uploading
                          </>
                        ) : (
                          'Upload Files'
                        )}
                      </button>
                    </>
                  ) : (
                    <div className="flex min-h-10 w-full items-center justify-center rounded-md bg-black text-zinc-400">
                      No files selected yet.
                    </div>
                  )}
                </div>

                {error && <div className="mt-2 text-red-500">{error}</div>}
                <div className="mt-4">
                  <h3 className="text-base font-semibold text-zinc-100">
                    Files
                  </h3>
                  {files.length === 0 ? (
                    <div className="flex min-h-10 w-full items-center justify-center rounded-md bg-black text-zinc-400">
                      No files uploaded yet.
                    </div>
                  ) : (
                    <ul className="mt-2 space-y-2">
                      {files.map((file) => {
                        const { color, icon } = getFileStatusStyles(
                          file.status,
                        );
                        return (
                          <li
                            key={file.file_id}
                            className="flex w-full items-center justify-between gap-2 overflow-hidden rounded-md bg-zinc-800 p-2 hover:bg-opacity-70"
                          >
                            <div className="flex min-w-0 max-w-[70%] items-center gap-2">
                              <FaFile className="flex-shrink-0 text-zinc-200" />
                              <button
                                onClick={() => handleDownload(file.file_id)}
                                className="xs:text-xs cursor-pointer truncate text-sm text-zinc-200 hover:underline sm:text-sm"
                                title={`${file.filename}\nUploaded at: ${file.timestamp}`}
                              >
                                {file.original_filename}
                              </button>
                            </div>

                            <div className="flex flex-shrink-0 items-center gap-2">
                              {file.status !== 'success' && (
                                <div className="flex flex-wrap items-center gap-1 text-xs sm:text-sm">
                                  <span className={`${color}`}>{icon}</span>
                                  <span className={`${color}`}>
                                    {file.status}
                                  </span>
                                </div>
                              )}

                              <button
                                onClick={() => handleDownload(file.file_id)}
                                className="rounded-lg p-1 text-zinc-300 hover:bg-zinc-700"
                              >
                                <FiDownload />
                              </button>
                              <button
                                onClick={() => handleDelete(file.file_id)}
                                className="rounded-lg p-1 text-zinc-300 hover:bg-zinc-700"
                                disabled={deletingFiles[file.file_id]}
                              >
                                {deletingFiles[file.file_id] ? (
                                  <FaSpinner className="animate-spin" />
                                ) : (
                                  <RxCross2 />
                                )}
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </>
            )}
            {selectedTab === 'select_file' && (
              <SelectDataSource closeModal={closeModal} />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DataSourceModal;
